import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useAppData } from '@/app-data';
import { useIdentity } from '@/features/identity/identity';
import { Item } from '@/features/backlog/types';
import { CLIENT_ID, getAccessToken } from '@/utils/http';

/** The real-time event envelope (mirrors the backend `defs.Event`). */
export interface RealtimeEvent {
  type: string;
  board_id: string;
  actor_id?: string | null;
  client_id?: string | null;
  payload?: unknown;
}
type ItemHandler = (ev: RealtimeEvent) => void;

interface RealtimeCtx {
  /** Member ids currently connected to the active board (includes you). */
  presence: string[];
  connected: boolean;
  /** Increments on every RE-connect (not the first connect); views refetch to catch up. */
  reconnectEpoch: number;
  /** Subscribe to item events for the active board; returns an unsubscribe. */
  subscribeItems: (fn: ItemHandler) => () => void;
}

const Ctx = createContext<RealtimeCtx | null>(null);

export const useRealtime = (): RealtimeCtx => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useRealtime must be used within RealtimeProvider');
  return v;
};

// ws(s):// URL for the active board, derived from the runtime apiUrl. Under OIDC the ID token
// rides as ?access_token= (browsers can't set an Authorization header on a WS handshake); the
// hub verifies it and derives the member. In legacy mode there's no token and actor_id is used.
const wsUrl = (boardId: string, actorId: string, accessToken: string | null): string => {
  const base = window.config.apiUrl.replace(/^http/, 'ws');
  const u = new URL(`${base}/ws`);
  u.searchParams.set('board_id', boardId);
  if (actorId) u.searchParams.set('actor_id', actorId);
  if (accessToken) u.searchParams.set('access_token', accessToken);
  return u.toString();
};

/**
 * Holds one WebSocket to the CURRENT board and turns it into (a) live presence and
 * (b) an item-event stream views subscribe to. Reconnects with backoff on drop, and
 * re-subscribes (leave old board / join new) whenever the board changes. Must sit
 * inside AppDataProvider (current board + members) and IdentityProvider (who you are).
 */
export const RealtimeProvider = ({ children }: { children: ReactNode }) => {
  const { currentBoardId, members, reloadMembers } = useAppData();
  const { me } = useIdentity();
  const meId = me?.id ?? ''; // sent as the WS actor so presence knows who you are

  const [presence, setPresence] = useState<string[]>([]);
  const [connected, setConnected] = useState(false);
  const [reconnectEpoch, setReconnectEpoch] = useState(0);
  // Subscribers held in a ref so publishing to them never re-renders this provider.
  const subs = useRef(new Set<ItemHandler>());

  const subscribeItems = useCallback((fn: ItemHandler) => {
    subs.current.add(fn);
    return () => {
      subs.current.delete(fn);
    };
  }, []);

  useEffect(() => {
    if (!currentBoardId) {
      setPresence([]);
      setConnected(false);
      return undefined;
    }
    let ws: WebSocket | null = null;
    let closedByUs = false;
    let didConnect = false; // first open is initial load, not a reconnect
    let retry = 0;
    let timer: number | undefined;

    const connect = () => {
      // Read the token at connect time (not as an effect dep) so silent token renewals don't
      // churn the socket, while a reconnect after a drop still uses the freshest token.
      ws = new WebSocket(wsUrl(currentBoardId, meId, getAccessToken()));
      ws.onopen = () => {
        retry = 0;
        setConnected(true);
        if (didConnect) setReconnectEpoch((e) => e + 1); // a genuine reconnect → views catch up
        didConnect = true;
      };
      ws.onmessage = (e) => {
        let ev: RealtimeEvent;
        try {
          ev = JSON.parse(e.data as string);
        } catch {
          return;
        }
        if (ev.type === 'presence.sync') {
          setPresence((ev.payload as { members?: string[] })?.members ?? []);
          return;
        }
        subs.current.forEach((fn) => fn(ev));
      };
      ws.onclose = () => {
        setConnected(false);
        if (closedByUs) return;
        retry += 1;
        const delay = Math.min(1000 * 2 ** Math.min(retry, 4), 15000);
        timer = window.setTimeout(connect, delay);
      };
      ws.onerror = () => ws?.close();
    };
    connect();

    return () => {
      closedByUs = true;
      if (timer) window.clearTimeout(timer);
      setPresence([]);
      setConnected(false);
      ws?.close();
    };
  }, [currentBoardId, meId]);

  // A member who signs in AFTER this tab loaded won't be in `members`, so their
  // presence avatar can't resolve — refetch members once per unknown id we see.
  const attemptedUnknown = useRef(new Set<string>());
  useEffect(() => {
    const known = new Set(members.map((m) => m.id));
    const missing = presence.filter((id) => id && !known.has(id) && !attemptedUnknown.current.has(id));
    if (missing.length) {
      missing.forEach((id) => attemptedUnknown.current.add(id));
      reloadMembers();
    }
  }, [presence, members, reloadMembers]);

  return (
    <Ctx.Provider value={{ presence, connected, reconnectEpoch, subscribeItems }}>{children}</Ctx.Provider>
  );
};

/**
 * Keeps a view's `items` in sync with live edits from OTHER tabs/people: it applies
 * upserts/deletes broadcast for `boardId` into local state, skipping THIS tab's own
 * echoes (by client id, so a second tab of the same member still updates). On a
 * reconnect it refetches via `reload` to recover events missed during the outage.
 */
export function useLiveItems(
  boardId: string | null,
  setItems: Dispatch<SetStateAction<Item[]>>,
  reload?: () => void,
): void {
  const { subscribeItems, reconnectEpoch } = useRealtime();

  useEffect(() => {
    if (!boardId) return undefined;
    return subscribeItems((ev) => {
      if (ev.board_id !== boardId) return;
      if (ev.client_id && ev.client_id === CLIENT_ID) return; // our own tab already applied it
      if (ev.type === 'item.upsert') {
        const it = ev.payload as Item;
        if (!it || it.board_id !== boardId) return;
        setItems((cur) => {
          const idx = cur.findIndex((x) => x.id === it.id);
          if (idx === -1) return [...cur, it];
          const next = cur.slice();
          next[idx] = it;
          return next;
        });
      } else if (ev.type === 'item.delete') {
        const ids = new Set(((ev.payload as { ids?: string[] })?.ids ?? []) as string[]);
        if (ids.size) setItems((cur) => cur.filter((x) => !ids.has(x.id)));
      }
    });
  }, [boardId, subscribeItems, setItems]);

  // Catch up after a genuine reconnect (events broadcast during the gap were missed).
  useEffect(() => {
    if (!boardId || reconnectEpoch === 0) return;
    reload?.();
    // Fire only on reconnect ticks (and only while a board is loaded).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconnectEpoch]);
}
